import { Link } from 'react-router-dom';
import {
  INDICATIVE_RATE_PCT,
  DEFAULT_TERM_MONTHS,
  formatCurrency,
  weeklyRepayment,
  totalInterest,
} from '../../lib/finance';
import { Faq } from '../../components/Faq';

/**
 * Everything on /calculator that is not the calculator.
 *
 * WHY THIS EXISTS:
 *
 * "car loan calculator nz" and its variants are the highest-volume, lowest-difficulty terms in NZ
 * vehicle finance, and the live SERP is not defended: an unbranded utility site sits at #2, a single
 * Mazda dealership at #8, and Ford, Toyota and Suzuki widgets fill 15–18. The incumbent's one
 * calculator page earns roughly 3,000 non-brand visits a month.
 *
 * A slider alone does not beat those — every one of them has a slider. What none of them has is an
 * honest answer to what the number MEANS: that a longer term makes the weekly figure smaller and
 * the loan more expensive, that the rate is an assumption rather than an offer, and what actually
 * moves it. That is the page worth linking to, and the passage worth quoting.
 *
 * Everything here is derived from `lib/finance.js`, so the prose can never quote a figure the
 * calculator above it disagrees with. Nothing here names a lender or claims a panel.
 */

const TERM_OPTIONS = [24, 36, 48, 60, 72, 84];
const EXAMPLE_LOAN = 30000;

export default function CalculatorGuide() {
  return (
    <>
      {/* ---- What the number actually costs -------------------------------------------------- */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-outfit text-2xl md:text-3xl font-bold text-black mb-4">
            A smaller weekly payment usually costs more
          </h2>
          <p className="text-gray-700 leading-relaxed mb-3">
            Stretching a loan over more years brings the weekly figure down, which is why it is the
            first thing you will be offered. It also means paying interest for longer. Here is the
            same {formatCurrency(EXAMPLE_LOAN)} loan at {INDICATIVE_RATE_PCT}% over different terms —
            the weekly number falls, the total climbs.
          </p>

          <div className="overflow-x-auto mt-8 border border-gray-200 rounded-lg">
            <table className="w-full text-left min-w-[420px]">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="p-4 font-outfit text-sm font-semibold text-black">Term</th>
                  <th className="p-4 font-outfit text-sm font-semibold text-black">Weekly</th>
                  <th className="p-4 font-outfit text-sm font-semibold text-black">Interest paid</th>
                </tr>
              </thead>
              <tbody>
                {TERM_OPTIONS.map((months) => {
                  const isDefault = months === DEFAULT_TERM_MONTHS;
                  return (
                    <tr
                      key={months}
                      className={`border-b border-gray-100 last:border-0 ${isDefault ? 'bg-[#00D47E]/5' : ''}`}
                    >
                      <td className="p-4 text-gray-800">
                        {months / 12} years
                        {isDefault && <span className="ml-2 text-xs text-gray-500">(shown above)</span>}
                      </td>
                      <td className="p-4 text-gray-800 tabular-nums">
                        {formatCurrency(weeklyRepayment(EXAMPLE_LOAN, months))}
                      </td>
                      <td className="p-4 text-gray-800 tabular-nums">
                        {formatCurrency(totalInterest(EXAMPLE_LOAN, months))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p className="text-gray-700 leading-relaxed mt-6">
            Going from three years to seven on this example saves about{' '}
            {formatCurrency(weeklyRepayment(EXAMPLE_LOAN, 36) - weeklyRepayment(EXAMPLE_LOAN, 84))} a
            week and costs about{' '}
            {formatCurrency(totalInterest(EXAMPLE_LOAN, 84) - totalInterest(EXAMPLE_LOAN, 36))} more
            in interest. Neither is automatically the right answer — a payment you can comfortably
            meet matters more than a number on a page — but you should be choosing between them
            knowingly.
          </p>
        </div>
      </section>

      {/* ---- What moves your rate ------------------------------------------------------------ */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-outfit text-2xl md:text-3xl font-bold text-black mb-4">
            What actually changes the rate you are offered
          </h2>
          <p className="text-gray-700 leading-relaxed mb-8">
            The {INDICATIVE_RATE_PCT}% above is an assumption used to do the arithmetic, not a rate
            anyone has offered you. Real offers vary considerably, and mostly on these:
          </p>

          <dl className="flex flex-col gap-6">
            {[
              ['Your credit history', 'The single biggest factor. A clean record with no missed payments opens the lower end of the range; recent defaults narrow it sharply, though they rarely close it entirely.'],
              ['What you can actually afford', 'Lenders weigh your income against your existing commitments — rent or mortgage, other loans, buy-now-pay-later, dependants. Two people on the same salary can be offered very different rates.'],
              ['The vehicle itself', 'Age, kilometres and type all count, because the car is the security. An older import is assessed differently from a late-model dealer car, which is one reason a bank and a specialist lender can quote you very differently for the same purchase.'],
              ['Your deposit', 'More deposit means borrowing less against the same asset, which lowers the lender’s risk. It also shortens the period where you owe more than the car is worth.'],
              ['The term', 'Longer terms sometimes carry a slightly higher rate as well as more interest overall — see the table above.'],
            ].map(([term, detail]) => (
              <div key={term}>
                <dt className="font-outfit font-semibold text-black mb-1">{term}</dt>
                <dd className="text-gray-700 leading-relaxed m-0">{detail}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* ---- Fees ---------------------------------------------------------------------------- */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-outfit text-2xl md:text-3xl font-bold text-black mb-4">
            The costs a repayment figure leaves out
          </h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            The calculator above shows principal and interest. A real contract usually adds some of
            the following, and they are worth asking about before you sign rather than after:
          </p>
          <ul className="flex flex-col gap-3 text-gray-700 leading-relaxed list-disc pl-6">
            <li><strong>An establishment fee</strong> — a one-off charge for setting the loan up.</li>
            <li><strong>An account or monthly fee</strong> — small, but it runs for the whole term.</li>
            <li><strong>Security registration</strong> — the cost of recording the lender&rsquo;s interest on the Personal Property Securities Register.</li>
            <li><strong>Add-ons</strong> — mechanical breakdown insurance, payment protection, extended warranties. These are optional. They are also where a cheap-looking loan often stops being cheap, so price them separately from the finance.</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mt-6">
            Under the Credit Contracts and Consumer Finance Act, a lender has to disclose all of this
            in writing before you commit. Read that disclosure — it is the document that tells you
            what the loan really costs, and the annual rate on it is the number to compare.
          </p>
        </div>
      </section>

      <Faq
        id="calculator-faq"
        title="Questions people ask about car loan repayments"
        items={[
          {
            q: 'How is the weekly repayment worked out?',
            a: (
              <>
                It is a standard amortising loan calculation: the amount you borrow, spread over the
                term, at {INDICATIVE_RATE_PCT}% p.a. Each payment covers the interest accrued since
                the last one, and whatever is left reduces the balance — so early payments are mostly
                interest and later ones mostly principal.
              </>
            ),
          },
          {
            q: 'Is this a quote?',
            a: (
              <>
                No. It is arithmetic on an assumed rate, not an offer, and no lender has seen your
                details. Actual rates depend on your circumstances and are set by the lender. See our{' '}
                <Link to="/disclaimer" className="text-[#00D47E] hover:underline">disclaimer</Link>{' '}
                for what that means in full.
              </>
            ),
          },
          {
            q: 'Does checking affect my credit score?',
            a: (
              <>
                No. Moving these sliders runs no credit check and leaves no mark on your file — the
                figures are worked out in your browser, and the numbers you enter are not sent to us
                or to any lender. (Like most sites, we record anonymous page visits in Google
                Analytics; that is a visit count, not your inputs. See our{' '}
                <Link to="/privacy" className="text-[#00D47E] hover:underline">privacy policy</Link>.)
                Our two-minute eligibility check also runs no credit check. A lender may run one
                later, only if you choose to make a full application, and you will be told first.
              </>
            ),
          },
          {
            q: 'How much deposit do I need?',
            a: (
              <>
                There is no universal minimum, and some lending is available with none. A deposit is
                still worth having: it reduces what you borrow, usually improves the rate you are
                offered, and shortens the stretch where the loan is larger than the car is worth.
              </>
            ),
          },
          {
            q: 'Can I pay the loan off early?',
            a: (
              <>
                Usually yes, and it saves interest, because interest accrues on the balance
                outstanding. Some contracts charge an early repayment fee. Ask before you sign — the
                answer varies between lenders and is required to be in your disclosure statement.
              </>
            ),
          },
          {
            q: 'Weekly, fortnightly or monthly — does it matter?',
            a: (
              <>
                Only slightly. Paying weekly or fortnightly reduces the balance a little sooner, so
                you pay marginally less interest overall. The bigger decision by far is the term.
              </>
            ),
          },
        ]}
      />
    </>
  );
}
